import React, { useState } from 'react'

import { OmForm, OmFormItem, OmSwitch, OmDescriptions, OmDescriptionsItem } from '@owma/kit'
import { ReactSortable } from 'react-sortablejs'

import classNames from 'classnames'
import i18n from 'i18n'

import { CONTACT_TYPE, MAILBOX_TYPE } from 'popups/Settings/def'

import { confirmBase } from 'utils/ui'

import dragHandleSvg from 'assets/images/button-drag-handle-24.svg'

import styles from './style.module.scss'

interface SelectContactsOptions {
  title?: string
  initValues: number[]
  okText?: string
  onOk?: (result: { checkedValues: number[] }, closeFunc: () => void) => void
  onCancel?: (reason: string) => void
  afterClose?: () => void
}

interface ExtObj {
  initValues: number[]
  checkedValues: number[]
  isChanged: boolean
  modalObj: any
  onChangeValue: (key: number, value: boolean) => void
  updateModal: () => void
  render: () => React.ReactElement
}

export function selectContacts(opts0: SelectContactsOptions): ExtObj {
  const opts: SelectContactsOptions = opts0 || { initValues: [] }

  const validIndexList = Object.keys(CONTACT_TYPE.typeDef).map((key) => {
    return CONTACT_TYPE.typeDef[key].value
  })

  const extObj: ExtObj = {
    initValues: [...opts.initValues],
    checkedValues: [...opts.initValues],
    isChanged: false,
    modalObj: null,
    onChangeValue: (key: number, value: boolean) => {
      const { checkedValues, initValues } = extObj
      const pos = checkedValues.indexOf(key)
      if (value === true && pos < 0) {
        checkedValues.push(key)
      } else if (value !== true && pos >= 0) {
        checkedValues.splice(pos, 1)
      }

      extObj.checkedValues = extObj.checkedValues
        .filter((value) => {
          return validIndexList.indexOf(value) >= 0
        })
        .sort()

      let isChanged = false
      if (checkedValues.length !== initValues.length) {
        isChanged = true
      } else {
        isChanged = initValues.some((key0) => {
          const pos = checkedValues.indexOf(key0)
          if (pos < 0) {
            return true
          }
          return false
        })
      }

      extObj.isChanged = isChanged
      extObj.updateModal()
    },
    updateModal: () => {},
    render: () => <></>,
  }

  interface SwitchValueProps {
    vkey: number
    initValue: boolean
  }

  const SwitchValue: React.FC<SwitchValueProps> = (props) => {
    const { vkey } = props
    const [checked, setChecked] = useState(props.initValue)

    const handleChange = (checked0: boolean) => {
      setChecked(checked0)
      extObj.onChangeValue(vkey, checked0)
    }

    return <OmSwitch checked={checked} onChange={handleChange} />
  }

  extObj.render = () => {
    const contactCheckElems = Object.keys(CONTACT_TYPE.typeDef).map((key) => {
      const contactInfo = CONTACT_TYPE.typeDef[key]
      const { labelKey, value } = contactInfo

      /* i18next-extract-disable-next-line */
      const label = i18n.t(labelKey)
      const initValue = extObj.initValues.indexOf(value) >= 0

      return (
        <OmDescriptionsItem label={label} key={key}>
          <SwitchValue vkey={value} initValue={initValue} />
        </OmDescriptionsItem>
      )
    })

    return (
      <React.Fragment>
        <div className={classNames(styles.subTitle)}>{opts.title}</div>
        <OmDescriptions className={classNames(styles.contactList)} size="small" column={1}>
          {contactCheckElems}
        </OmDescriptions>
      </React.Fragment>
    )
  }

  const modalCfg: any = {
    // title: opts.title,
    wrapClassName: classNames(styles.selectContacts, 'settingsConfirm'),
    icon: null,
    content: extObj.render(),
    okText: opts.okText || i18n.t('common.button.ok'),
    cancelText: i18n.t('common.button.cancel'),
    maskTransitionName: 'maskTransitionName',
    width: 380,
    // closable: true,
    maskClosable: true,
    centered: true,
    okButtonProps: {
      disabled: extObj.isChanged !== true,
      className: classNames('type1', 'h38'),
      style: { minWidth: '100px' },
    },
    cancelButtonProps: {
      className: classNames('h38'),
      style: { minWidth: '100px' },
    },
    onOk: (closeFunc: () => void) => {
      if (opts.onOk != null) {
        opts.onOk(
          {
            checkedValues: extObj.checkedValues,
          },
          closeFunc,
        )
      } else {
        closeFunc()
      }
    },
    onCancel: () => {
      if (opts.onCancel != null) {
        opts.onCancel('cancel')
      }
    },
    afterClose: () => {
      if (opts.afterClose != null) {
        opts.afterClose()
      }
    },
  }

  extObj.modalObj = confirmBase(modalCfg)

  extObj.updateModal = () => {
    extObj.modalObj.updateBase({
      content: extObj.render(),
      okButtonProps: {
        disabled: extObj.isChanged !== true,
        className: classNames('type1', 'h38'),
        style: { minWidth: '100px' },
      },
    })
  }

  return extObj
}

interface SortableItem {
  value: number
}

interface ChangeContactsOrderOptions {
  title?: string
  initValues: number[]
  okText?: string
  onOk?: (result: { list: number[] }, closeFunc: () => void) => void
  onCancel?: (reason: string) => void
  afterClose?: () => void
}

interface OrderExtObj {
  initValues: number[]
  sortedValues: SortableItem[]
  isChanged: boolean
  modalObj: any
  getList: () => number[]
  orderChanged: (newList: SortableItem[]) => void
  updateModal: () => void
  render: () => React.ReactElement
}

export function changeContactsOrder(opts0: ChangeContactsOrderOptions): OrderExtObj {
  const opts: ChangeContactsOrderOptions = opts0 || { initValues: [] }

  const initValues = [...opts.initValues]

  // 초기값에 빠진 항목이 있는 경우 추가.
  Object.keys(CONTACT_TYPE.typeDef).forEach((key) => {
    const contactInfo = CONTACT_TYPE.typeDef[key]
    const { value } = contactInfo

    const pos = initValues.indexOf(value)
    if (pos < 0) {
      initValues.push(value)
    }
  })

  // CONTACT_TYPE 에 없는 항목은 제거.
  for (let i = initValues.length - 1; i >= 0; i -= 1) {
    const value = initValues[i]
    const key = CONTACT_TYPE.getKeyByValue(value)
    if (key == null) {
      initValues.splice(i, 1)
    }
  }

  const extObj: OrderExtObj = {
    initValues: [...initValues],
    // sortablejs 에서 사용하기 위해 object 형태로 변환.
    sortedValues: [
      ...initValues.map((value) => {
        return { value }
      }),
    ],
    isChanged: false,
    modalObj: null,
    getList: () => {
      return extObj.sortedValues.map((value) => {
        return value.value
      })
    },
    orderChanged: (newList: SortableItem[]) => {
      extObj.sortedValues = [...newList]

      const list = extObj.getList()

      let isChanged = false

      if (list.length !== extObj.initValues.length) {
        isChanged = true
      }

      isChanged = list.some((value, index) => {
        return value !== extObj.initValues[index]
      })

      extObj.isChanged = isChanged

      extObj.updateModal()
    },
    updateModal: () => {},
    render: () => <></>,
  }

  extObj.render = () => {
    const contactCheckElems = extObj.sortedValues.map((value) => {
      const key = CONTACT_TYPE.getKeyByValue(value.value)
      const labelKey = CONTACT_TYPE.getLabelKey(key)

      /* i18next-extract-disable-next-line */
      const label = i18n.t(labelKey)

      return (
        <OmFormItem label={label} key={key}>
          <img src={dragHandleSvg} className="dragHandle" alt="dragHandle" />
        </OmFormItem>
      )
    })

    const formAttrs = {
      className: classNames(styles.contactList),
      layout: 'horizontal' as const,
    }

    return (
      <React.Fragment>
        <div className={classNames(styles.subTitle)}>{opts.title}</div>
        <OmForm {...formAttrs}>
          <ReactSortable
            handle=".dragHandle"
            list={extObj.sortedValues as any}
            setList={extObj.orderChanged as any}
          >
            {contactCheckElems}
          </ReactSortable>
        </OmForm>
      </React.Fragment>
    )
  }

  const modalCfg: any = {
    // title: opts.title,
    wrapClassName: classNames(styles.changeContactsOrder, 'settingsConfirm'),
    icon: null,
    content: extObj.render(),
    okText: opts.okText || i18n.t('common.button.ok'),
    cancelText: i18n.t('common.button.cancel'),
    maskTransitionName: 'maskTransitionName',
    width: 380,
    // closable: true,
    maskClosable: true,
    centered: true,
    okButtonProps: {
      disabled: extObj.isChanged !== true,
      className: classNames('type1', 'h38'),
      style: { minWidth: '100px' },
    },
    cancelButtonProps: {
      className: classNames('h38'),
      style: { minWidth: '100px' },
    },
    onOk: (closeFunc: () => void) => {
      if (opts.onOk != null) {
        opts.onOk(
          {
            list: extObj.getList(),
          },
          closeFunc,
        )
      } else {
        closeFunc()
      }
    },
    onCancel: () => {
      if (opts.onCancel != null) {
        opts.onCancel('cancel')
      }
    },
    afterClose: () => {
      if (opts.afterClose != null) {
        opts.afterClose()
      }
    },
  }

  extObj.modalObj = confirmBase(modalCfg)

  extObj.updateModal = () => {
    extObj.modalObj.updateBase({
      content: extObj.render(),
      okButtonProps: {
        disabled: extObj.isChanged !== true,
        className: classNames('type1', 'h38'),
        style: { minWidth: '100px' },
      },
    })
  }

  return extObj
}

interface SelectMailboxTypesOptions {
  title?: string
  initValues: number[]
  okText?: string
  onOk?: (result: { checkedValues: number[] }, closeFunc: () => void) => void
  onCancel?: (reason: string) => void
  afterClose?: () => void
}

export function selectMailboxTypes(opts0: SelectMailboxTypesOptions): ExtObj {
  const opts: SelectMailboxTypesOptions = opts0 || { initValues: [] }

  const validIndexList = Object.keys(MAILBOX_TYPE.typeDef).map((key) => {
    return MAILBOX_TYPE.typeDef[key].value
  })

  const extObj: ExtObj = {
    initValues: [...opts.initValues],
    checkedValues: [...opts.initValues],
    isChanged: false,
    modalObj: null,
    onChangeValue: (key: number, value: boolean) => {
      const { checkedValues, initValues } = extObj
      const pos = checkedValues.indexOf(key)
      if (value === true && pos < 0) {
        checkedValues.push(key)
      } else if (value !== true && pos >= 0) {
        checkedValues.splice(pos, 1)
      }

      extObj.checkedValues = extObj.checkedValues
        .filter((value) => {
          return validIndexList.indexOf(value) >= 0
        })
        .sort()

      let isChanged = false
      if (checkedValues.length !== initValues.length) {
        isChanged = true
      } else {
        isChanged = initValues.some((key0) => {
          const pos = checkedValues.indexOf(key0)
          if (pos < 0) {
            return true
          }
          return false
        })
      }

      extObj.isChanged = isChanged
      extObj.updateModal()
    },
    updateModal: () => {},
    render: () => <></>,
  }

  interface SwitchValueProps {
    vkey: number
    initValue: boolean
  }

  const SwitchValue: React.FC<SwitchValueProps> = (props) => {
    const { vkey } = props
    const [checked, setChecked] = useState(props.initValue)

    const handleChange = (checked0: boolean) => {
      setChecked(checked0)
      extObj.onChangeValue(vkey, checked0)
    }

    return <OmSwitch checked={checked} onChange={handleChange} />
  }

  extObj.render = () => {
    const mailboxTypesElems = Object.keys(MAILBOX_TYPE.typeDef).map((key) => {
      const mailboxTypeInfo = MAILBOX_TYPE.typeDef[key]
      const { labelKey, value } = mailboxTypeInfo

      /* i18next-extract-disable-next-line */
      const label = i18n.t(labelKey)
      const initValue = extObj.initValues.indexOf(value) >= 0

      return (
        <OmDescriptionsItem label={label} key={key}>
          <SwitchValue vkey={value} initValue={initValue} />
        </OmDescriptionsItem>
      )
    })

    return (
      <OmDescriptions className={classNames(styles.mailboxTypeList)} size="small" column={1}>
        {mailboxTypesElems}
      </OmDescriptions>
    )
  }

  const modalCfg: any = {
    title: opts.title,
    wrapClassName: classNames(styles.selectMailboxTypes, 'settingsConfirm'),
    icon: null,
    content: extObj.render(),
    okText: opts.okText || i18n.t('common.button.ok'),
    cancelText: i18n.t('common.button.cancel'),
    maskTransitionName: 'maskTransitionName',
    width: 380,
    // closable: true,
    maskClosable: true,
    centered: true,
    okButtonProps: {
      disabled: extObj.isChanged !== true,
      className: classNames('type1', 'h38'),
      style: { minWidth: '100px' },
    },
    cancelButtonProps: {
      className: classNames('h38'),
      style: { minWidth: '100px' },
    },
    onOk: (closeFunc: () => void) => {
      if (opts.onOk != null) {
        opts.onOk(
          {
            checkedValues: extObj.checkedValues,
          },
          closeFunc,
        )
      } else {
        closeFunc()
      }
    },
    onCancel: () => {
      if (opts.onCancel != null) {
        opts.onCancel('cancel')
      }
    },
    afterClose: () => {
      if (opts.afterClose != null) {
        opts.afterClose()
      }
    },
  }

  extObj.modalObj = confirmBase(modalCfg)

  extObj.updateModal = () => {
    extObj.modalObj.updateBase({
      content: extObj.render(),
      okButtonProps: {
        disabled: extObj.isChanged !== true,
        className: classNames('type1', 'h38'),
        style: { minWidth: '100px' },
      },
    })
  }

  return extObj
}

const popupProps = {
  selectContacts,
  changeContactsOrder,
  selectMailboxTypes,
}

export default popupProps
